import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ThreeDots } from 'react-loader-spinner';

import SideBar from '../Sidebar';
import SampleRequest from '../SampleRequest';
import ApiDocumentationContent from '../ApiDocumentationContent';
import LanguageSelector from '../../LanguageSelector';
import { CORDER_ARTICLE_CONTENT } from '../../../static/ApiData/corder-api.constants';
import { formatApiDocText } from '../../../utils/formatApiDocText';

import logo from '../../../assets/images/logo.svg';
import hamBurger from '../../../assets/images/hamburger.svg';

const SIDEBAR_BREAKPOINT = 960;

const ApiDocumentation = () => {
	const sectionRefs = useRef({});
	const contentRef = useRef(null);
	const { t } = useTranslation();
	const [isSidebarOpen, setIsSidebarOpen] = useState(false);
	const [loading, setLoading] = useState(true);
	const [selectedArticle, setSelectedArticle] = useState(null);
	const [hash, setHash] = useState(null);
	const [activeGroup, setActiveGroup] = useState(null);

	const [isMobileLayout, setIsMobileLayout] = useState(
		typeof window !== 'undefined' ? window.innerWidth < SIDEBAR_BREAKPOINT : false
	);

	useEffect(() => {
		const handleResize = () => {
			const mobile = window.innerWidth < SIDEBAR_BREAKPOINT;
			setIsMobileLayout(mobile);
			if (!mobile) setIsSidebarOpen(false);
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		setLoading(true);
		const article = CORDER_ARTICLE_CONTENT.find((item) => item.portal === 'corder_api');
		setSelectedArticle(article);

		if (article?.content?.length) {
			const hashValue =
				typeof window !== 'undefined' ? window.location.hash.substring(1) : '';

			if (hashValue) {
				const group = article.content.find((item) => item.id === hashValue);
				if (group) {
					setActiveGroup(group.id);
				} else {
					const parentGroup = article.content.find((item) =>
						item.subSections?.some((sub) => sub.id === hashValue)
					);
					setActiveGroup(parentGroup ? parentGroup.id : article.content[0]?.id);
				}
			} else {
				setActiveGroup(article.content[0]?.id);
			}
		}

		setLoading(false);
	}, []);

	const scrollContentTo = (el, offset = 0) => {
		if (!contentRef.current || !el) return;
		const containerTop = contentRef.current.getBoundingClientRect().top;
		const elTop = el.getBoundingClientRect().top;
		const scrollTop = contentRef.current.scrollTop + (elTop - containerTop) - offset;
		contentRef.current.scrollTo({ top: scrollTop, behavior: 'smooth' });
	};

	const handleScrollToTop = () => {
		if (contentRef.current) {
			contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
		}
		if (typeof window !== 'undefined' && window.location.hash) {
			window.history.replaceState(
				null,
				'',
				window.location.pathname + window.location.search
			);
		}
	};

	useEffect(() => {
		const hashValue =
			typeof window !== 'undefined' ? window.location.hash.substring(1) : '';
		setHash(hashValue);

		if (hashValue && selectedArticle) {
			const group = selectedArticle.content.find((item) => item.id === hashValue);

			if (group) {
				setActiveGroup(group.id);
				setTimeout(() => {
					const el = sectionRefs.current[group.id];
					scrollContentTo(el);
				}, 100);
			} else {
				const parentGroup = selectedArticle.content.find((item) =>
					item.subSections?.some((sub) => sub.id === hashValue)
				);
				if (parentGroup) {
					setActiveGroup(parentGroup.id);
					setTimeout(() => {
						scrollContentTo(document.getElementById(hashValue));
					}, 200);
				}
			}
		}
	}, [hash, selectedArticle]);

	const handleApiTypeSelection = (apiTypeId) => {
		if (typeof window === 'undefined' || !selectedArticle) return;

		window.history.replaceState(
			null,
			'',
			`${window.location.pathname}${window.location.search}#${apiTypeId}`
		);
		setHash(apiTypeId);

		const group = selectedArticle.content.find((item) => item.id === apiTypeId);

		if (group) {
			setActiveGroup(group.id);
			setTimeout(() => {
				scrollContentTo(sectionRefs.current[group.id]);
			}, 100);
			return;
		}

		const parentGroup = selectedArticle.content.find((item) =>
			item.subSections?.some((sub) => sub.id === apiTypeId)
		);
		if (parentGroup) {
			setActiveGroup(parentGroup.id);
			setTimeout(() => {
				scrollContentTo(document.getElementById(apiTypeId));
			}, 200);
		}
	};

	const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

	if (loading) {
		return (
			<div className='w-full h-screen flex justify-center items-center'>
				<ThreeDots
					visible={true}
					height='80'
					width='80'
					color='#fdc137'
					radius='9'
					ariaLabel='three-dots-loading'
				/>
			</div>
		);
	}

	return (
		<div className='flex h-screen overflow-hidden font-sans'>
			{isMobileLayout && (
				<div
					className='flex items-center w-full border-b border-[#D1D1D1] bg-white'
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						right: 0,
						zIndex: 60,
						height: '56px',
						overflow: 'visible',
					}}
				>
					<button
						onClick={toggleSidebar}
						className='flex items-center justify-center ml-4 cursor-pointer bg-transparent border-0 p-0'
						aria-label='Toggle sidebar'
					>
						<img src={hamBurger} alt='menu' className='w-6 m-0' />
					</button>

					<Link
						to='/'
						className='flex flex-1 gap-2 px-4 flex-row justify-center items-center no-underline text-black'
					>
						<img alt='header' src={logo} className='w-8 m-0' />
						<div className='text-lg font-extrabold text-gray-900'>
							Mereos
						</div>
					</Link>

					<div className='mr-4'>
						<LanguageSelector compact hideLabelOnMobile />
					</div>
				</div>
			)}

			<div
				style={{
					...(isMobileLayout
						? {
							position: 'fixed',
							top: '56px',
							left: 0,
							width: '240px',
							height: 'calc(100% - 56px)',
							transform: isSidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
							transition: 'transform 0.3s ease',
							zIndex: 50,
							overflowY: 'auto',
							backgroundColor: '#fff',
						}
						: {
							position: 'sticky',
							top: 0,
							width: '240px',
							minWidth: '240px',
							height: '100vh',
							overflowY: 'auto',
							backgroundColor: '#fff',
							flexShrink: 0,
						}),
				}}
			>
				<SideBar
					hash={hash}
					onSelectApiType={(id) => {
						handleApiTypeSelection(id);
						if (isMobileLayout) setIsSidebarOpen(false);
					}}
					onScrollToTop={() => {
						handleScrollToTop();
						if (isMobileLayout) setIsSidebarOpen(false);
					}}
				/>
			</div>

			<div
				ref={contentRef}
				style={{
					flex: 1,
					height: '100vh',
					overflowY: 'auto',
					paddingBottom: '40px',
					paddingTop: isMobileLayout ? '56px' : '0',
				}}
			>
				{!isMobileLayout && (
					<div className='flex justify-end items-center p-[27px_11px_26px_11px] border-b border-[#ddd]'>
						<LanguageSelector />
					</div>
				)}

				<div>
					<h1 className='text-3xl font-bold text-gray-800 text-center md:text-start mb-2 ml-3 mt-8'>
						{t(selectedArticle?.portal)}
					</h1>
					<div className='md:ml-3 p-6 rounded-lg w-[98%] mb-4 mt-3 bg-[#fff8e1] border-l-4 border-[#fdc137] shadow-md'>
						<p className='w-full text-gray-600 max-w-full'>
							{formatApiDocText(t(selectedArticle?.description))}
						</p>
					</div>

					{selectedArticle?.content.map((group) => (
						<div key={group.id} className='w-[99%] mx-auto md:mr-0'>
							<div
								ref={(el) => {
									sectionRefs.current[group.id] = el;
								}}
								id={group.id}
								className='scroll-mt-4'
							>
								<h2 className='text-center md:text-start text-2xl font-bold text-gray-800 mb-2 mt-4'>
									{t(group.heading)}
								</h2>
								<div className='p-6 rounded-lg w-[98%] mb-4 mt-3 bg-[#fff8e1] border-l-4 border-[#fdc137] shadow-md'>
									{group.groupDescription && (
										<p className='text-gray-600 max-w-full'>
											{formatApiDocText(t(group.groupDescription))}
										</p>
									)}
									<div className='mt-2 text-sm text-gray-500'>
										{group.subSections?.length || 0} endpoints
									</div>
								</div>
							</div>

							{group.subSections?.map((article) => (
								<div className='flex w-full justify-between' key={article.id} id={article.id}>
									<div
										style={{
											width: isMobileLayout ? '100%' : '49%',
										}}
									>
										<ApiDocumentationContent content={article} />
									</div>
									{!isMobileLayout && (
										<div className='w-[49%] bg-[#2D3134] overflow-hidden shadow-none'>
											{article.requestObject && (
												<SampleRequest sampleObject={article.requestObject} />
											)}
										</div>
									)}
								</div>
							))}
						</div>
					))}
				</div>
			</div>

			{isMobileLayout && isSidebarOpen && (
				<div
					onClick={toggleSidebar}
					role='presentation'
					style={{
						position: 'fixed',
						inset: 0,
						backgroundColor: 'rgba(0,0,0,0.5)',
						zIndex: 40,
						top: '56px',
					}}
				/>
			)}
		</div>
	);
};

export default ApiDocumentation;
